import { Card, CardContent } from '@/components/ui/card';
import ListSavedCard from '@/features/List/components/ListSavedCard';
import useGetSavedLists from '@/features/List/hooks/useGetSavedLists';
import { ListSaved } from '@/features/List/list';
import dayjs from 'dayjs';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const getNextEvents = (listsSaved: ListSaved[]) => {
	const today = dayjs();
	const thisWeekEnd = today.add(7, 'day');
	const thisMonthEnd = today.add(30, 'day');

	const getAdjustedBirthday = (birthday: string) => {
		const currentYearBirthday = dayjs(birthday).set('year', today.year());
		return currentYearBirthday.isBefore(today)
			? currentYearBirthday.add(1, 'year')
			: currentYearBirthday;
	};

	const todayEvents = listsSaved
		.filter((list) => {
			const birthday = getAdjustedBirthday(list.user.birthday as string);
			return birthday.isSame(today, 'day');
		})
		.sort((a, b) => {
			const birthdayA = getAdjustedBirthday(a.user.birthday as string);
			const birthdayB = getAdjustedBirthday(b.user.birthday as string);
			return birthdayA.diff(birthdayB);
		});

	const weekEvents = listsSaved
		.filter((list) => {
			const birthday = getAdjustedBirthday(list.user.birthday as string);
			return birthday.isAfter(today) && birthday.isBefore(thisWeekEnd);
		})
		.sort((a, b) => {
			const birthdayA = getAdjustedBirthday(a.user.birthday as string);
			const birthdayB = getAdjustedBirthday(b.user.birthday as string);
			return birthdayA.diff(birthdayB);
		});

	const monthEvents = listsSaved
		.filter((list) => {
			const birthday = getAdjustedBirthday(list.user.birthday as string);
			return (
				birthday.isAfter(thisWeekEnd) && birthday.isBefore(thisMonthEnd)
			);
		})
		.sort((a, b) => {
			const birthdayA = getAdjustedBirthday(a.user.birthday as string);
			const birthdayB = getAdjustedBirthday(b.user.birthday as string);
			return birthdayA.diff(birthdayB);
		});

	return { todayEvents, weekEvents, monthEvents };
};

const NextEvents = () => {
	const { t } = useTranslation();
	const { listsSaved } = useGetSavedLists();
	const navigate = useNavigate();

	// const nextChristmas = dayjs()
	// 	.endOf('year')
	// 	.set('month', 11)
	// 	.set('date', 25);

	const { todayEvents, weekEvents, monthEvents } = getNextEvents(listsSaved);

	return (
		<>
			<h2 className="text-xl">{t('events.title')}</h2>
			{todayEvents.length > 0 && (
				<div className="mb-2 mt-2">
					<p className="mb-1 text-gray-500 dark:text-gray-400">
						{t('events.today')}
					</p>
					<div className="flex flex-col gap-2">
						{todayEvents.map((list) => (
							<ListSavedCard
								key={list.id}
								savedList={list}
								onClick={() =>
									navigate(`/shared/${list.public_uid}`)
								}
							/>
						))}
					</div>
				</div>
			)}
			{weekEvents.length > 0 && (
				<div className="mb-2 mt-2">
					<p className="mb-1 text-gray-500 dark:text-gray-400">
						{t('events.thisWeek')}
					</p>
					<div className="flex flex-col gap-2">
						{weekEvents.map((list) => (
							<ListSavedCard
								key={list.id}
								savedList={list}
								onClick={() =>
									navigate(`/shared/${list.public_uid}`)
								}
							/>
						))}
					</div>
				</div>
			)}
			{monthEvents.length > 0 && (
				<div className="mb-2 mt-2">
					<p className="mb-1 text-gray-500 dark:text-gray-400">
						{t('events.thisMonth')}
					</p>
					<div className="flex flex-col gap-2">
						{monthEvents.map((list) => (
							<ListSavedCard
								key={list.id}
								savedList={list}
								onClick={() =>
									navigate(`/shared/${list.public_uid}`)
								}
							/>
						))}
					</div>
				</div>
			)}
			{todayEvents.length === 0 &&
				weekEvents.length === 0 &&
				monthEvents.length === 0 && (
					<Card className="mx-auto w-full max-w-md sm:m-0 sm:max-w-md">
						<CardContent className="flex flex-col items-center justify-center space-y-4 p-6 text-center">
							<div className="rounded-full bg-muted p-3">
								<Calendar className="h-8 w-8 text-muted-foreground" />
							</div>
							<h3 className="text-lg font-semibold">
								{t('events.noEvents')}
							</h3>
							<p className="text-sm text-muted-foreground">
								{t('events.noEventsDescription')}
							</p>
						</CardContent>
					</Card>
				)}

			{/* <Button className="w-full" variant="secondary">
				{t('events.seeAll')}
			</Button> */}
		</>
	);
};

export default NextEvents;
